import { accessScope, type Action, type Me, type Module, type Role } from '@repo/shared';

export interface NavItem {
  key: string;
  icon: string;
  to: string;
  module: Module;
  action?: Action;
  /** Also shown when the user only has "own" access (e.g. a teacher's own grade sheets). */
  allowOwn?: boolean;
  /** Restrict to these roles in addition to the permission check. */
  roles?: Role[];
  /** Delivery phase, shown on the placeholder page until the module ships. */
  phase: number;
}

export interface NavGroup {
  key: string;
  items: NavItem[];
}

/** Side navigation from design/project/sc - App.dc.html (NAV), filtered by the tenant permission matrix. */
export const NAV: NavGroup[] = [
  {
    key: 'pilotage',
    items: [{ key: 'dashboard', icon: 'space_dashboard', to: '/', module: 'dashboard', phase: 7 }],
  },
  {
    key: 'admissions',
    items: [
      {
        key: 'preinscriptions',
        icon: 'app_registration',
        to: '/preinscriptions',
        module: 'admissions',
        phase: 4,
      },
      { key: 'candidatures', icon: 'assignment_ind', to: '/candidatures', module: 'admissions', phase: 4 },
    ],
  },
  {
    key: 'scolarite',
    items: [
      // Teachers (own scope) see the students of their groups.
      { key: 'students', icon: 'groups', to: '/etudiants', module: 'students', allowOwn: true, phase: 1 },
      { key: 'teachers', icon: 'school', to: '/enseignants', module: 'students', action: 'edit', phase: 9 },
      {
        key: 'curriculum',
        icon: 'account_tree',
        to: '/formations',
        module: 'curriculum',
        allowOwn: true,
        phase: 1,
      },
      {
        key: 'timetable',
        icon: 'calendar_month',
        to: '/emploi-du-temps',
        module: 'timetable',
        allowOwn: true,
        phase: 6,
      },
      {
        key: 'absences',
        icon: 'event_busy',
        to: '/absences',
        module: 'attendance',
        allowOwn: true,
        phase: 5,
      },
      { key: 'calendar', icon: 'event', to: '/calendrier', module: 'announcements', phase: 9 },
      { key: 'announcements', icon: 'campaign', to: '/annonces', module: 'announcements', phase: 6 },
    ],
  },
  {
    key: 'evaluation',
    items: [
      {
        key: 'grades',
        icon: 'edit_note',
        to: '/notes',
        module: 'grades',
        allowOwn: true,
        phase: 2,
      },
      {
        key: 'rollCall',
        icon: 'checklist',
        to: '/appel',
        module: 'attendance',
        allowOwn: true,
        roles: ['ENSEIGNANT'],
        phase: 5,
      },
      { key: 'deliberations', icon: 'gavel', to: '/deliberations', module: 'deliberations', phase: 2 },
      // Teachers (own scope): the internships they supervise or judge.
      { key: 'internships', icon: 'work', to: '/stages', module: 'internships', allowOwn: true, phase: 10 },
      { key: 'documents', icon: 'description', to: '/documents', module: 'documents', phase: 3 },
    ],
  },
  {
    key: 'finance',
    items: [
      { key: 'schedules', icon: 'receipt_long', to: '/finance/echeanciers', module: 'finance', phase: 5 },
      { key: 'unpaid', icon: 'money_off', to: '/finance/impayes', module: 'finance', phase: 5 },
    ],
  },
  {
    key: 'administration',
    items: [
      { key: 'settings', icon: 'settings', to: '/parametres', module: 'settings', action: 'edit', phase: 7 },
      { key: 'audit', icon: 'policy', to: '/journal', module: 'audit', phase: 9 },
    ],
  },
];

export function canSee(me: Me, item: NavItem): boolean {
  if (item.roles && !item.roles.some((r) => me.roles.includes(r))) return false;
  const scope = accessScope(me.permissions, item.module, item.action ?? 'read');
  return scope === 'all' || (scope === 'own' && item.allowOwn === true);
}

export function navFor(me: Me): NavGroup[] {
  return NAV.map((g) => ({ ...g, items: g.items.filter((i) => canSee(me, i)) })).filter(
    (g) => g.items.length > 0,
  );
}

export const allNavItems = NAV.flatMap((g) => g.items.map((i) => ({ ...i, group: g.key })));
